import React from "react";
import { Link } from "react-router-dom";
import {
  Globe,
  Briefcase,
  Code2,
  TrendingUp,
  Building2,
  ShieldCheck,
  Home,
  Info,
  Phone,
  BookOpen,
  FileText,
  Lock,
  ScrollText,
  RotateCcw,
  AlertCircle,
} from "lucide-react";

import { FaInstagram, FaLinkedin, FaYoutube, FaFacebook } from "react-icons/fa";
import Chatbot from "./Chatbot";

function Footer() {
  const services = [
    {
      title: "Web Development",
      path: "/web-development",
      icon: Code2,
    },
    {
      title: "Interior Designing",
      path: "/interior-designing",
      icon: TrendingUp,
    },
    {
      title: "Digital Marketing",
      path: "/digital-marketing",
      icon: Globe,
    },
  ];

  const quickLinks = [
    {
      title: "About",
      path: "https://webxsecure.in/about",
      icon: Info,
    },

    {
      title: "Case Study",
      path: "https://webxsecure.in/case-study",
      icon: FileText,
    },
    {
      title: "Blog",
      path: "https://webxsecure.in/blog",
      icon: BookOpen,
    },
    {
      title: "Contact",
      path: "https://webxsecure.in/contact",
      icon: Phone,
    },
  ];

  const legalLinks = [
    {
      title: "Privacy Policy",
      path: "/privacy-policy",
      icon: Lock,
    },
    {
      title: "Terms & Conditions",
      path: "/terms-and-conditions",
      icon: ScrollText,
    },
    {
      title: "Refund Policy",
      path: "/refund-policy",
      icon: RotateCcw,
    },
    {
      title: "Disclaimer",
      path: "/disclaimer",
      icon: AlertCircle,
    },
  ];

  const socialLinks = [
    {
      icon: FaInstagram,
      url: "https://www.instagram.com/webxsecure",
      color: "hover:text-pink-500",
      label: "Instagram",
    },
    {
      icon: FaLinkedin,
      url: "https://www.linkedin.com/company/webxsecure",
      color: "hover:text-blue-500",
      label: "LinkedIn",
    },
    {
      icon: FaYoutube,
      url: "https://www.youtube.com/@webxsecure",
      color: "hover:text-red-500",
      label: "YouTube",
    },
    {
      icon: FaFacebook,
      url: "https://www.facebook.com/webxsecure",
      color: "hover:text-blue-600",
      label: "Facebook",
    },
  ];

  return (
    <footer className="relative overflow-hidden bg-white py-10 text-gray-900 transition-all duration-300 dark:bg-[#050505] dark:text-white">
      <div className="absolute left-0 top-0 h-[450px] w-[450px] rounded-full bg-orange-300/10 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-[450px] w-[450px] rounded-full bg-primary/10 blur-[140px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-4">
          <div>
            <h2 className="flex items-center gap-2 text-2xl font-bold">
              <Building2 className="h-6 w-6 text-primary" />
              Web X Secure
            </h2>

            <p className="mt-5 text-base leading-relaxed text-gray-600 dark:text-white/60">
              Web X Secure specializes in website development, SEO, digital
              marketing, SaaS development, cybersecurity, and business
              automation solutions that help businesses grow faster in the
              digital world.
            </p>
          </div>

          <div>
            <h3 className="mb-5 flex items-center gap-2 text-lg font-semibold">
              <Briefcase className="h-5 w-5 text-primary" />
              Services
            </h3>

            <ul className="space-y-3 text-base text-gray-600 dark:text-white/70">
              {services.map((item) => (
                <li key={item.title}>
                  <Link
                    to={item.path}
                    className="flex items-center gap-2 transition hover:text-orange-500"
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-5 flex items-center gap-2 text-lg font-semibold">
              <Home className="h-5 w-5 text-primary" />
              Quick Links
            </h3>

            <ul className="space-y-3 text-base text-gray-600 dark:text-white/70">
              {quickLinks.map((item) => (
                <li key={item.title}>
                  <Link
                    to={item.path}
                    className="flex items-center gap-2 transition hover:text-orange-500"
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-5 flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Legal Links
            </h3>

            <ul className="space-y-3 text-base text-gray-600 dark:text-white/70">
              {legalLinks.map((item) => (
                <li key={item.title}>
                  <Link
                    to={item.path}
                    className="flex items-center gap-2 transition hover:text-orange-500"
                  >
                    <item.icon className="h-4 w-4" />
                    {item.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-6 border-t border-gray-200 pt-8 dark:border-white/10 sm:flex-row">
          <p className="text-sm text-gray-500 dark:text-white/50">
            © {new Date().getFullYear()} Web X Secure. All rights reserved.
          </p>

          <div className="flex gap-6 text-xl text-gray-600 dark:text-white/60">
            {socialLinks.map((item, index) => (
              <a
                key={index}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={item.label}
                className={`transition ${item.color}`}
              >
                <item.icon />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
